import React from 'react';
import { Message, User, CaptionMode } from '../../types/messaging';
import { format } from 'date-fns';
import VoiceMessageBubble from '../../features/voice-chat/components/VoiceMessageBubble';
import { ClickableText } from '../ClickableWord';

interface MessageBubbleProps {
    message: Message;
    isOwn: boolean;
    showAvatar?: boolean;
    otherParticipant?: User;
    /** Voice mode caption mode. When undefined, voice bubbles default to 'always'. */
    captionMode?: CaptionMode;
    /** True if this is the most recent bot voice reply (used for autoplay). */
    autoPlayLatest?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    isOwn,
    showAvatar = false,
    otherParticipant,
    captionMode = 'always',
    autoPlayLatest = false,
}) => {
    // Voice messages get their own bubble.
    const meta = (message.metadata ?? {}) as Record<string, unknown>;
    if (meta.isVoiceMode === true || message.audioUrl) {
        return (
            <VoiceMessageBubble
                message={message}
                isOwn={isOwn}
                showAvatar={showAvatar}
                otherParticipant={otherParticipant}
                captionMode={captionMode}
                autoPlay={autoPlayLatest && !isOwn}
            />
        );
    }

    // Words the bot explicitly taught this turn (e.g. English equivalent of
    // a CJK word the user dropped in). ClickableText only highlights at the
    // single-word grain, so split multi-word phrases like "street food" into
    // their constituent tokens.
    const taughtTermsRaw = Array.isArray(meta.taughtTerms)
        ? (meta.taughtTerms as string[])
        : [];
    const taughtTerms = taughtTermsRaw.flatMap((t) =>
        t.split(/\s+/).filter((w) => w.length > 0),
    );
    const time = format(new Date(message.createdAt), 'HH:mm');

    const displayName = message.sender?.fullName || message.sender?.username || otherParticipant?.fullName || otherParticipant?.username || '';
    const avatarUrl = message.sender?.avatarUrl || otherParticipant?.avatarUrl;
    const initial = displayName.charAt(0).toUpperCase();

    return (
        <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-1`}>
            <div className={`flex items-start gap-2 max-w-[75%] ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                {/* Avatar (for other's messages) */}
                {!isOwn && (
                    <div className="flex-shrink-0 w-8">
                        {showAvatar ? (
                            avatarUrl ? (
                                <img
                                    src={avatarUrl}
                                    alt={displayName}
                                    className="w-8 h-8 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-sm font-semibold">
                                    {initial}
                                </div>
                            )
                        ) : (
                            <div className="w-8 h-8" /> // Placeholder for alignment
                        )}
                    </div>
                )}

                {/* Message Content */}
                <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                    <div
                        className={`
                            px-4 py-2 rounded-2xl break-words
                            ${isOwn
                                ? 'bg-primary-500 text-white rounded-br-md'
                                : 'bg-gray-100 text-gray-900 rounded-bl-md'
                            }
                        `}
                    >
                        <p className="text-sm whitespace-pre-wrap">
                            <ClickableText
                                text={message.content}
                                highlightWords={taughtTerms}
                            />
                        </p>
                    </div>

                    {/* Time and Read Status */}
                    <div className={`flex items-center gap-1 mt-1 px-1 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                        <span className="text-xs text-gray-400">{time}</span>
                        {isOwn && (
                            <span className="text-xs">
                                {message.isRead ? (
                                    // Double checkmark for read
                                    <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                                    </svg>
                                ) : (
                                    // Single checkmark for sent
                                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                                    </svg>
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessageBubble;
